import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Minus, Plus, ShoppingCart, Tag, Sparkles, AlertCircle, Loader2 } from 'lucide-react';
import { useCartStore } from '../../../stores/cart.store';
import { useCalculateEligiblePromotions, type EligiblePromotion } from '../../../api/mutations/promotion.mutations';
import { useBranchStore } from '../../../stores/branch.store';
import { useAuthStore } from '../../../stores/auth.store';

interface CartPanelProps {
  onCheckout: () => void;
}

function CartPanel({ onCheckout }: CartPanelProps) {
  const { t, i18n } = useTranslation();
  const {
    items,
    getSubtotal,
    getTax,
    getTotal,
    updateQuantity,
    removeItem,
    clearCart,
    loyaltyPointsToRedeem,
    setLoyaltyPointsToRedeem,
    appliedPromotion,
    setAppliedPromotion,
  } = useCartStore();

  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const user = useAuthStore((state) => state.user);
  const calculateEligibleMutation = useCalculateEligiblePromotions();
  const [eligiblePromotions, setEligiblePromotions] = useState<EligiblePromotion[]>([]);

  // Fetch eligible promotions when cart changes
  useEffect(() => {
    const branchId = selectedBranchId || user?.branchId;

    if (!branchId || items.length === 0) {
      setEligiblePromotions([]);
      return;
    }

    const cartItems = items
      .filter(item => item.menuItemId && item.quantity > 0 && item.unitPrice > 0)
      .map(item => {
        // Handle categoryId - it might be a string or a populated object
        let categoryId = item.categoryId;
        if (categoryId && typeof categoryId === 'object' && '_id' in categoryId) {
          categoryId = (categoryId as any)._id;
        }
        return {
          menuItemId: item.menuItemId,
          categoryId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          size: item.size,
        };
      });

    if (cartItems.length === 0) {
      setEligiblePromotions([]);
      return;
    }

    calculateEligibleMutation.mutate(
      { items: cartItems, branchId },
      {
        onSuccess: (data) => {
          setEligiblePromotions(data.eligiblePromotions || []);
          // Auto-apply best promotion if available and not already applied
          if (data.bestAutoApply && !appliedPromotion) {
            setAppliedPromotion({
              promotionId: data.bestAutoApply.promotion._id,
              name: data.bestAutoApply.promotion.name,
              type: data.bestAutoApply.promotion.type,
              discountAmount: data.bestAutoApply.discountAmount,
            });
          }
        },
      }
    );
  }, [items, selectedBranchId, user?.branchId]);

  const getLocalizedName = (name: { en: string; th: string }) => {
    return i18n.language === 'th' ? name.th || name.en : name.en;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const subtotal = getSubtotal();
  const tax = getTax();
  const total = getTotal();
  const maxPointsToRedeem = Math.min(subtotal, loyaltyPointsToRedeem + 1000); // Example max

  // Check if branchId is available for promotions
  const branchId = selectedBranchId || user?.branchId;
  const isLoadingPromotions = calculateEligibleMutation.isPending;

  return (
    <div className="w-96 border-l bg-card flex flex-col">
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          <h2 className="text-lg font-bold">{t('pos.cart')}</h2>
          <span className="text-sm text-muted-foreground">({items.length})</span>
        </div>
        {items.length > 0 && (
          <button
            onClick={clearCart}
            className="text-sm text-destructive hover:underline tap-highlight-none"
          >
            {t('pos.clearCart')}
          </button>
        )}
      </div>

      {/* Cart items */}
      <div className="flex-1 overflow-y-auto p-4">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <ShoppingCart className="h-16 w-16 mb-4 opacity-20" />
            <p>{t('pos.emptyCart')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className="rounded-lg border bg-background p-3">
                {/* Row 1: Name and Delete button */}
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium text-sm truncate flex-1">{item.name}</h3>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-destructive/60 hover:text-destructive tap-highlight-none p-1 -mr-1"
                    aria-label="Remove item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Row 2: Size & Sweetness */}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {item.size.charAt(0).toUpperCase() + item.size.slice(1)} • {item.sweetness}% sweet
                </p>

                {/* Modifiers */}
                {item.modifiers && item.modifiers.length > 0 && (
                  <div className="mt-1.5 text-xs text-muted-foreground space-y-0.5">
                    {item.modifiers.map((mod, idx) => (
                      <div key={idx}>
                        + {mod.name} ({formatPrice(mod.priceAdjustment)})
                      </div>
                    ))}
                  </div>
                )}

                {/* Notes */}
                {item.notes && (
                  <div className="mt-1.5 text-xs italic text-muted-foreground">
                    Note: {item.notes}
                  </div>
                )}

                {/* Row 3: Quantity controls and Price */}
                <div className="flex items-center justify-between mt-3 pt-2 border-t">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      className="w-7 h-7 rounded border bg-secondary hover:bg-secondary/80 disabled:opacity-40 disabled:cursor-not-allowed tap-highlight-none flex items-center justify-center"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-8 text-center font-semibold text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 rounded border bg-secondary hover:bg-secondary/80 tap-highlight-none flex items-center justify-center"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <span className="font-bold text-primary">{formatPrice(item.totalPrice)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary and checkout */}
      {items.length > 0 && (
        <div className="border-t bg-card p-4 space-y-3">
          {/* No branch warning */}
          {!branchId && (
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t('pos.selectBranchForPromotions') || 'Select a branch to see available promotions'}
              </p>
            </div>
          )}

          {/* Loading promotions indicator */}
          {branchId && isLoadingPromotions && (
            <div className="flex items-center gap-2 rounded-lg bg-muted p-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                {t('pos.checkingPromotions') || 'Checking available promotions...'}
              </p>
            </div>
          )}

          {/* Applied Promotion Banner */}
          {appliedPromotion && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-3">
              <Tag className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-700 dark:text-green-300 truncate">
                  {getLocalizedName(appliedPromotion.name)}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {t('pos.youSave')} {formatPrice(appliedPromotion.discountAmount)} {t('pos.onBasePrices') || 'on base prices'}
                </p>
              </div>
              <button
                onClick={() => setAppliedPromotion(undefined)}
                className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 text-xs"
              >
                ✕
              </button>
            </div>
          )}

          {/* Other eligible promotions (if not applied) */}
          {!appliedPromotion && eligiblePromotions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {t('pos.availablePromotions')}
              </p>
              {eligiblePromotions.slice(0, 2).map((promo) => (
                <button
                  key={promo.promotion._id}
                  onClick={() => setAppliedPromotion({
                    promotionId: promo.promotion._id,
                    name: promo.promotion.name,
                    type: promo.promotion.type,
                    discountAmount: promo.discountAmount,
                  })}
                  className="w-full flex items-center gap-2 rounded-lg border border-dashed border-primary/50 bg-primary/5 p-2 hover:bg-primary/10 transition-colors text-left"
                >
                  <Tag className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {getLocalizedName(promo.promotion.name)}
                    </p>
                    <p className="text-xs text-primary">
                      {t('pos.save')} {formatPrice(promo.discountAmount)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Loyalty points input */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('pos.loyaltyPoints')} (1 pt = ฿1)
            </label>
            <input
              type="number"
              min="0"
              max={maxPointsToRedeem}
              value={loyaltyPointsToRedeem}
              onChange={(e) => setLoyaltyPointsToRedeem(Number(e.target.value))}
              className="w-full rounded border bg-background px-3 py-2 text-sm"
              placeholder="Enter points to redeem"
            />
          </div>

          {/* Totals */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('pos.subtotal')}</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            {appliedPromotion && (
              <div className="flex justify-between text-green-600 dark:text-green-400">
                <span>{t('pos.promotionDiscount')}</span>
                <span>-{formatPrice(appliedPromotion.discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('pos.tax')} (7%)</span>
              <span>{formatPrice(tax)}</span>
            </div>
            {loyaltyPointsToRedeem > 0 && (
              <div className="flex justify-between text-primary">
                <span>{t('pos.loyaltyDiscount')}</span>
                <span>-{formatPrice(loyaltyPointsToRedeem)}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between text-lg font-bold">
              <span>{t('pos.total')}</span>
              <span className="text-primary">{formatPrice(total)}</span>
            </div>
          </div>

          {/* Checkout button */}
          <button
            onClick={onCheckout}
            className="w-full rounded-lg bg-primary py-4 font-bold text-primary-foreground hover:bg-primary/90 transition-colors touch-target tap-highlight-none"
          >
            {t('pos.checkout')}
          </button>
        </div>
      )}
    </div>
  );
}

export default CartPanel;
